import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  X, AlertTriangle, RefreshCw, Check, LogIn, Package,
  TrendingDown, Truck, PackageCheck,
} from 'lucide-react';
import { PRODUCTOS_SIN_POLLO, CLAVES_FRIAS, CONVERSION } from '../constants/productos';
import { fmtMoney } from '../utils/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CALIENTES = PRODUCTOS_SIN_POLLO.filter(p => !CLAVES_FRIAS.has(p.clave));
const PRODUCTOS_MAP = Object.fromEntries(PRODUCTOS_SIN_POLLO.map(p => [p.clave, p]));

const fmtQty = (qty) => (Number(qty) % 1 !== 0 ? Number(qty).toFixed(1) : String(Number(qty)));

export function ControlRemotoModal({ punto, onClose, onRefresh, initialTab, initialMovTipo }) {
  const [estado, setEstado] = useState(null);
  const [loadingEstado, setLoadingEstado] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab || 'inicial');

  // Apertura de turno
  const [aperturaSubmitting, setAperturaSubmitting] = useState(false);

  // Reabrir día (corte erróneo sin ventas)
  const [reabrirSubmitting, setReabrirSubmitting] = useState(false);

  // Tab: Inventario Inicial
  const [inicialCantidades, setInicialCantidades] = useState(
    Object.fromEntries(Object.keys(CONVERSION).map(k => [k, '']))
  );
  const [inicialSubmitting, setInicialSubmitting] = useState(false);

  // Tab: Venta por Conteo
  const [conteo, setConteo] = useState({});
  const [ventaSubmitting, setVentaSubmitting] = useState(false);
  const [ventaResult, setVentaResult] = useState(null);

  // Tab: Movimiento — salida (tránsito a moto)
  const [movTipo, setMovTipo] = useState(initialMovTipo || 'salida');
  const [motas, setMotas] = useState([]);
  const [salidaDestino, setSalidaDestino] = useState('');
  const [salidaCantidades, setSalidaCantidades] = useState({});
  const [salidaSubmitting, setSalidaSubmitting] = useState(false);
  const [historialSalidas, setHistorialSalidas] = useState([]);

  // Tab: Movimiento — entrada (confirmar tránsito pendiente)
  const [pendingTransitos, setPendingTransitos] = useState([]);
  const [selectedTransito, setSelectedTransito] = useState(null);
  const [cantConfirmadas, setCantConfirmadas] = useState({});
  const [entradaSubmitting, setEntradaSubmitting] = useState(false);
  const [loadingTransitos, setLoadingTransitos] = useState(false);

  // Feedback compartido
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const clearFeedback = () => { setError(''); setSuccess(''); };

  const fetchEstado = useCallback(async () => {
    setLoadingEstado(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/estado-remoto/${punto.punto_venta_id}`);
      setEstado(data);
      setActiveTab(initialTab || (data.tiene_inventario ? 'venta' : 'inicial'));
    } catch {
      setError('Error al cargar estado del puesto');
    } finally {
      setLoadingEstado(false);
    }
  }, [punto.punto_venta_id, initialTab]);

  const loadMovimientoData = useCallback(async () => {
    setLoadingTransitos(true);
    try {
      const [puntosRes, transitosRes, salidasRes] = await Promise.all([
        axios.get(`${API}/puntos-venta`),
        axios.get(`${API}/transitos/pendientes/${punto.punto_venta_id}`),
        axios.get(`${API}/transitos/historial-salidas/${punto.punto_venta_id}`),
      ]);
      setMotas(puntosRes.data.filter(p => p.categoria === 'moto' && p.id !== punto.punto_venta_id));
      setPendingTransitos(transitosRes.data);
      setHistorialSalidas(salidasRes.data);
    } catch {
      // silent
    } finally {
      setLoadingTransitos(false);
    }
  }, [punto.punto_venta_id]);

  useEffect(() => { fetchEstado(); }, [fetchEstado]);

  useEffect(() => {
    if (activeTab === 'movimiento') loadMovimientoData();
  }, [activeTab, loadMovimientoData]);

  // Calcula ventas en tiempo real: disponible - conteo ingresado
  const ventasCalculadas = useMemo(() => {
    if (!estado?.disponible) return {};
    const result = {};
    for (const p of CALIENTES) {
      const cv = conteo[p.clave];
      if (cv === undefined || cv === '') continue;
      const disp = estado.disponible[p.clave] || 0;
      const venta = disp - (parseFloat(cv) || 0);
      if (venta > 0) result[p.clave] = venta;
    }
    return result;
  }, [estado, conteo]);

  const totalVenta = useMemo(() => {
    return Object.entries(ventasCalculadas).reduce((sum, [clave, qty]) => {
      return sum + qty * (parseFloat(estado?.precios?.[clave]) || 0);
    }, 0);
  }, [ventasCalculadas, estado]);

  // ── Handlers ──

  const handleReabrirDia = async () => {
    clearFeedback();
    setReabrirSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/reabrir-dia/${punto.punto_venta_id}`);
      setSuccess(data.message);
      await fetchEstado();
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al reabrir el día');
    } finally {
      setReabrirSubmitting(false);
    }
  };

  const handleApertura = async () => {
    clearFeedback();
    setAperturaSubmitting(true);
    try {
      await axios.post(`${API}/admin/apertura-remota`, { punto_venta_id: punto.punto_venta_id });
      setSuccess('Turno aperturado correctamente');
      await fetchEstado();
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al aperturar turno');
    } finally {
      setAperturaSubmitting(false);
    }
  };

  const handleInventarioInicial = async () => {
    clearFeedback();
    const productos = {};
    for (const [clave, val] of Object.entries(inicialCantidades)) {
      const n = parseFloat(val);
      if (!isNaN(n) && n > 0) productos[clave] = n;
    }
    if (Object.keys(productos).length === 0) { setError('Ingresa al menos una cantidad'); return; }
    setInicialSubmitting(true);
    try {
      await axios.post(`${API}/admin/inventario-inicial-remoto`, {
        punto_venta_id: punto.punto_venta_id,
        productos,
      });
      setSuccess('Inventario inicial registrado');
      setInicialCantidades(Object.fromEntries(Object.keys(CONVERSION).map(k => [k, ''])));
      await fetchEstado();
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al registrar inventario inicial');
    } finally {
      setInicialSubmitting(false);
    }
  };

  const handleVenta = async () => {
    clearFeedback();
    setVentaResult(null);
    if (Object.keys(ventasCalculadas).length === 0) {
      setError('No hay ventas calculadas. Verifica el conteo ingresado.');
      return;
    }
    setVentaSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/venta-remota`, {
        punto_venta_id: punto.punto_venta_id,
        conteo_fisico: conteo,
      });
      setVentaResult(data);
      setSuccess(`Venta registrada · ${fmtMoney(data.total, 0)}`);
      setConteo({});
      await fetchEstado();
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al registrar venta');
    } finally {
      setVentaSubmitting(false);
    }
  };

  const handleTransitoRemoto = async () => {
    clearFeedback();
    if (!salidaDestino) { setError('Selecciona una moto destino'); return; }
    const productos = {};
    for (const [clave, val] of Object.entries(salidaCantidades)) {
      const n = parseFloat(val);
      if (!isNaN(n) && n > 0) productos[clave] = n;
    }
    if (Object.keys(productos).length === 0) { setError('Selecciona al menos un producto'); return; }
    setSalidaSubmitting(true);
    try {
      await axios.post(`${API}/admin/transito-remoto`, {
        origen_id: punto.punto_venta_id,
        destino_id: salidaDestino,
        productos,
      });
      setSuccess('Tránsito registrado. La moto deberá confirmar recepción.');
      setSalidaCantidades({});
      setSalidaDestino('');
      await fetchEstado();
      await loadMovimientoData();
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al registrar tránsito');
    } finally {
      setSalidaSubmitting(false);
    }
  };

  const handleConfirmarEntrada = async () => {
    clearFeedback();
    const total = Object.values(cantConfirmadas).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    if (total === 0) { setError('Confirma al menos una cantidad'); return; }
    setEntradaSubmitting(true);
    try {
      await axios.post(`${API}/admin/confirmar-entrada-remota/${selectedTransito.id}`, {
        productos_confirmados: cantConfirmadas,
      });
      setSuccess('Entrada confirmada. Inventarios actualizados.');
      setSelectedTransito(null);
      setCantConfirmadas({});
      await fetchEstado();
      await loadMovimientoData();
      onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al confirmar entrada');
    } finally {
      setEntradaSubmitting(false);
    }
  };

  const canOperate = estado && !estado.dia_cerrado && !estado.turno_atrasado && estado.tiene_turno_hoy;

  const tabs = estado?.tiene_inventario
    ? [{ id: 'venta', label: 'Venta' }, { id: 'movimiento', label: 'Entrada/Salida' }]
    : [{ id: 'inicial', label: 'Inventario Inicial' }];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl flex flex-col" style={{ maxHeight: '92vh' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0 border-b border-zinc-100">
          <div>
            <p className="text-xs text-zinc-400 font-medium uppercase tracking-wide">Control Remoto</p>
            <h2 className="font-bold text-zinc-900 text-base leading-tight">{punto.punto_venta_nombre}</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {loadingEstado ? (
            <div className="flex justify-center py-10">
              <span className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
            </div>
          ) : !estado ? (
            <p className="text-sm text-red-600 text-center py-6">Error al cargar estado</p>
          ) : (
            <>
              {/* ── Avisos de estado ── */}

              {estado.turno_atrasado && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-amber-800">Turno atrasado — {estado.fecha_activa}</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Cierra el turno anterior desde el panel principal antes de operar hoy.
                    </p>
                  </div>
                </div>
              )}

              {estado.dia_cerrado && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-2">
                  <p className="text-xs font-bold text-red-800">Día cerrado — no se pueden registrar operaciones</p>
                  <p className="text-xs text-red-700">Fecha: {estado.fecha_activa}</p>
                  {estado.corte_tiene_ventas ? (
                    <p className="text-xs text-red-700">
                      Este corte ya tiene ventas registradas, no se puede reabrir desde aquí. Contacta a desarrollo si es necesario revertirlo.
                    </p>
                  ) : (
                    <>
                      <p className="text-xs text-red-700">
                        Este corte no tiene ventas registradas — probablemente se cerró antes de operar. Puedes reabrir el día para permitir operaciones.
                      </p>
                      <button
                        onClick={handleReabrirDia}
                        disabled={reabrirSubmitting}
                        className="w-full h-9 bg-red-600 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-40"
                      >
                        {reabrirSubmitting
                          ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <>Reabrir día</>}
                      </button>
                    </>
                  )}
                </div>
              )}

              {!estado.turno_atrasado && !estado.dia_cerrado && !estado.tiene_turno_hoy && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold text-blue-800">Sin turno abierto hoy</p>
                    <p className="text-xs text-blue-600 mt-0.5">Apertura el turno para registrar operaciones.</p>
                  </div>
                  <button
                    onClick={handleApertura}
                    disabled={aperturaSubmitting}
                    className="shrink-0 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-40"
                  >
                    {aperturaSubmitting
                      ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <><LogIn className="w-3.5 h-3.5" /> Aperturar turno</>}
                  </button>
                </div>
              )}

              {canOperate && !estado.tiene_inventario && (
                <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3">
                  <p className="text-xs text-zinc-600">Turno abierto · Registra el inventario inicial para continuar.</p>
                </div>
              )}

              {canOperate && estado.tiene_inventario && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-emerald-800">Turno activo · {estado.fecha_activa}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                      {CALIENTES.filter(p => (estado.disponible?.[p.clave] || 0) > 0).map(p => (
                        <span key={p.clave} className="text-xs text-emerald-700">
                          {p.short}: <strong>{estado.disponible[p.clave]}</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                  <button onClick={fetchEstado} className="w-7 h-7 flex items-center justify-center rounded text-emerald-600 hover:bg-emerald-100 shrink-0">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Feedback */}
              {error && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
              )}
              {success && (
                <p className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 font-medium">{success}</p>
              )}

              {/* ── Operaciones ── */}
              {canOperate && (
                <>
                  {/* Tabs */}
                  <div className="flex gap-1 border-b border-zinc-200 -mx-5 px-5">
                    {tabs.map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => { setActiveTab(tab.id); clearFeedback(); setVentaResult(null); }}
                        className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                          activeTab === tab.id
                            ? 'border-zinc-900 text-zinc-900'
                            : 'border-transparent text-zinc-500 hover:text-zinc-700'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* ── Tab: Inventario Inicial ── */}
                  {activeTab === 'inicial' && (
                    <div className="space-y-3 pt-1">
                      <p className="text-xs text-zinc-500">
                        Ingresa las cantidades de cada pieza que tiene el puesto al inicio del turno.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {CALIENTES.map(p => (
                          <div key={p.clave} className="space-y-0.5">
                            <label className="text-xs text-zinc-500 font-medium block">{p.nombre}</label>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setInicialCantidades(prev => ({
                                  ...prev, [p.clave]: Math.max(0, (parseFloat(prev[p.clave]) || 0) - 1)
                                }))}
                                className="w-8 h-8 flex items-center justify-center border border-zinc-300 rounded text-zinc-600 active:bg-zinc-100"
                              >-</button>
                              <input
                                type="number" inputMode="numeric" min="0"
                                value={inicialCantidades[p.clave]}
                                onChange={e => setInicialCantidades(prev => ({ ...prev, [p.clave]: e.target.value }))}
                                placeholder="0"
                                className="flex-1 h-8 text-center border border-zinc-300 rounded text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400"
                              />
                              <button
                                onClick={() => setInicialCantidades(prev => ({
                                  ...prev, [p.clave]: (parseFloat(prev[p.clave]) || 0) + 1
                                }))}
                                className="w-8 h-8 flex items-center justify-center border border-zinc-300 rounded text-zinc-600 active:bg-zinc-100"
                              >+</button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handleInventarioInicial}
                        disabled={inicialSubmitting}
                        className="w-full h-10 bg-zinc-900 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
                      >
                        {inicialSubmitting
                          ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <><Package className="w-4 h-4" /> Registrar inventario inicial</>}
                      </button>
                    </div>
                  )}

                  {/* ── Tab: Venta por Conteo ── */}
                  {activeTab === 'venta' && (
                    <div className="space-y-3 pt-1">
                      <p className="text-xs text-zinc-500">
                        Ingresa lo que el puesto reporta tener actualmente.
                        La diferencia con el inventario del sistema se registra como venta.
                      </p>

                      <div className="overflow-x-auto -mx-1">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-zinc-400">
                              <th className="text-left pb-1.5 font-medium">Pieza</th>
                              <th className="text-center pb-1.5 font-medium w-14">Sist.</th>
                              <th className="text-center pb-1.5 font-medium w-24">Reporta</th>
                              <th className="text-center pb-1.5 font-medium w-14">Venta</th>
                              <th className="text-right pb-1.5 font-medium w-20">Importe</th>
                            </tr>
                          </thead>
                          <tbody>
                            {CALIENTES.map(p => {
                              const disp = estado.disponible?.[p.clave] || 0;
                              const cv = conteo[p.clave];
                              const conteoNum = (cv !== undefined && cv !== '') ? (parseFloat(cv) || 0) : null;
                              const ventaQty = conteoNum !== null ? Math.max(0, disp - conteoNum) : 0;
                              const precio = parseFloat(estado.precios?.[p.clave]) || 0;
                              const importe = ventaQty * precio;
                              const esNegativo = conteoNum !== null && conteoNum > disp;

                              return (
                                <tr key={p.clave} className="border-t border-zinc-100">
                                  <td className="py-1.5 font-medium text-zinc-700">{p.nombre}</td>
                                  <td className="text-center py-1.5 font-mono font-bold text-zinc-500">{disp}</td>
                                  <td className="text-center py-1.5 px-1">
                                    <input
                                      type="number" inputMode="numeric" min="0"
                                      value={cv ?? ''}
                                      onChange={e => {
                                        clearFeedback(); setVentaResult(null);
                                        setConteo(prev => ({ ...prev, [p.clave]: e.target.value }));
                                      }}
                                      placeholder={String(disp)}
                                      className={`w-full h-7 text-center border rounded text-sm font-mono focus:outline-none focus:ring-1 ${
                                        esNegativo ? 'border-amber-400 bg-amber-50 focus:ring-amber-400' : 'border-zinc-300 focus:ring-zinc-400'
                                      }`}
                                    />
                                  </td>
                                  <td className={`text-center py-1.5 font-mono font-bold ${ventaQty > 0 ? 'text-zinc-900' : 'text-zinc-300'}`}>
                                    {conteoNum !== null ? ventaQty : '--'}
                                  </td>
                                  <td className={`text-right py-1.5 font-mono ${importe > 0 ? 'text-zinc-900 font-bold' : 'text-zinc-300'}`}>
                                    {conteoNum !== null && precio > 0 ? fmtMoney(importe, 0) : '--'}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="border-t-2 border-zinc-200">
                              <td colSpan={4} className="py-2 text-xs text-zinc-500 font-medium">Total venta</td>
                              <td className="text-right py-2 font-mono font-bold text-base text-zinc-900">
                                {fmtMoney(totalVenta, 0)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {CALIENTES.some(p => {
                        const cv = conteo[p.clave];
                        const disp = estado.disponible?.[p.clave] || 0;
                        return cv !== undefined && cv !== '' && (parseFloat(cv) || 0) > disp;
                      }) && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-800">
                            Algunas cantidades superan el inventario calculado.
                            Puede indicar entradas no registradas. Se registra solo lo vendido.
                          </p>
                        </div>
                      )}

                      {ventaResult && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-1">
                          <p className="text-xs font-bold text-emerald-800">Última venta registrada</p>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                            {Object.entries(ventaResult.ventas || {}).map(([clave, qty]) => {
                              const p = CALIENTES.find(x => x.clave === clave);
                              return p ? (
                                <span key={clave} className="text-xs text-emerald-700">
                                  {p.short}: <strong>{qty}</strong>
                                </span>
                              ) : null;
                            })}
                          </div>
                          <p className="text-sm font-bold text-emerald-900">Total: {fmtMoney(ventaResult.total, 0)}</p>
                        </div>
                      )}

                      <button
                        onClick={handleVenta}
                        disabled={ventaSubmitting || Object.keys(ventasCalculadas).length === 0}
                        className="w-full h-10 bg-emerald-600 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
                      >
                        {ventaSubmitting
                          ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <><TrendingDown className="w-4 h-4" /> Registrar venta</>}
                      </button>
                    </div>
                  )}

                  {/* ── Tab: Entrada/Salida (tránsitos) ── */}
                  {activeTab === 'movimiento' && (
                    <div className="space-y-3 pt-1">

                      {/* Toggle salida/entrada */}
                      <div className="flex rounded-lg border border-zinc-200 overflow-hidden">
                        {[
                          { id: 'salida', label: 'Salida a moto' },
                          { id: 'entrada', label: 'Confirmar entrada' },
                        ].map(({ id, label }) => (
                          <button
                            key={id}
                            onClick={() => {
                              setMovTipo(id); clearFeedback();
                              setSelectedTransito(null); setCantConfirmadas({});
                            }}
                            className={`flex-1 py-2 text-xs font-medium transition-colors ${
                              movTipo === id
                                ? id === 'salida' ? 'bg-orange-500 text-white' : 'bg-blue-600 text-white'
                                : 'text-zinc-500 hover:bg-zinc-50'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>

                      {loadingTransitos ? (
                        <div className="flex justify-center py-6">
                          <span className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
                        </div>
                      ) : (
                        <>
                          {/* ══ SALIDA: crear tránsito hacia moto ══ */}
                          {movTipo === 'salida' && (
                            <div className="space-y-3">

                              {/* Selector de moto */}
                              <div>
                                <p className="text-xs text-zinc-500 font-medium mb-1.5">Destino (moto)</p>
                                {motas.length === 0 ? (
                                  <p className="text-xs text-zinc-400">No hay motos disponibles como destino</p>
                                ) : (
                                  <div className="flex flex-wrap gap-2">
                                    {motas.map(m => (
                                      <button
                                        key={m.id}
                                        onClick={() => setSalidaDestino(m.id)}
                                        className={`px-3 h-8 rounded-lg border text-xs font-medium transition-colors ${
                                          salidaDestino === m.id
                                            ? 'border-zinc-900 bg-zinc-900 text-white'
                                            : 'border-zinc-300 text-zinc-700 hover:border-zinc-500'
                                        }`}
                                      >
                                        {m.name}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Cantidades por pieza */}
                              <div className="space-y-0.5">
                                {CALIENTES.map(p => {
                                  const disp = estado.disponible?.[p.clave] || 0;
                                  const qty = parseFloat(salidaCantidades[p.clave] || 0);
                                  const step = p.allowHalf ? 0.5 : 1;
                                  return (
                                    <div key={p.clave} className="flex items-center justify-between py-2 border-b border-zinc-100">
                                      <div>
                                        <p className="text-sm font-medium text-zinc-900">{p.nombre}</p>
                                        <p className="text-xs text-zinc-400">Disponible: {disp}</p>
                                      </div>
                                      <div className="flex items-center gap-1.5">
                                        <button
                                          onClick={() => setSalidaCantidades(prev => ({
                                            ...prev, [p.clave]: Math.max(0, Math.round((qty - step) * 2) / 2)
                                          }))}
                                          disabled={qty === 0}
                                          className="w-9 h-9 flex items-center justify-center border border-zinc-300 rounded-lg disabled:opacity-30 text-zinc-600"
                                        >-</button>
                                        <span className={`w-10 text-center text-xl font-bold ${qty > 0 ? 'text-orange-600' : 'text-zinc-300'}`}>
                                          {fmtQty(qty)}
                                        </span>
                                        <button
                                          onClick={() => setSalidaCantidades(prev => ({
                                            ...prev, [p.clave]: Math.min(disp, Math.round((qty + step) * 2) / 2)
                                          }))}
                                          disabled={qty >= disp}
                                          className="w-9 h-9 flex items-center justify-center border border-zinc-300 rounded-lg disabled:opacity-30 text-zinc-600"
                                        >+</button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              <button
                                onClick={handleTransitoRemoto}
                                disabled={salidaSubmitting || !salidaDestino || !CALIENTES.some(p => parseFloat(salidaCantidades[p.clave] || 0) > 0)}
                                className="w-full h-10 bg-orange-500 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
                              >
                                {salidaSubmitting
                                  ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  : <><Truck className="w-4 h-4" /> Enviar en tránsito</>}
                              </button>

                              {/* Historial de salidas del turno */}
                              {historialSalidas.length > 0 && (
                                <div className="pt-1">
                                  <p className="text-xs text-zinc-400 font-medium mb-2 uppercase tracking-wide">Salidas del turno</p>
                                  <div className="space-y-2">
                                    {historialSalidas.map(s => {
                                      const badge = {
                                        recibido:  { cls: 'bg-green-100 text-green-700', label: 'Recibido' },
                                        cancelado: { cls: 'bg-zinc-100 text-zinc-400',   label: 'Cancelado' },
                                      }[s.status] ?? { cls: 'bg-amber-100 text-amber-700', label: 'En tránsito' };
                                      return (
                                        <div key={s.id} className={`p-2.5 rounded-xl border text-xs ${s.status === 'cancelado' ? 'bg-zinc-50 border-zinc-100 opacity-60' : 'bg-zinc-50 border-zinc-200'}`}>
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="font-medium text-zinc-800">{s.destino_nombre}</span>
                                            <span className={`px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                                          </div>
                                          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                                            {CALIENTES.filter(p => s.productos?.[p.clave]).map(p => (
                                              <span key={p.clave} className="text-zinc-600">
                                                {p.short}: <strong>{fmtQty(s.productos[p.clave])}</strong>
                                              </span>
                                            ))}
                                          </div>
                                          {s.confirmado_por_nombre && (
                                            <p className="text-zinc-400 mt-0.5">Confirmado por {s.confirmado_por_nombre}</p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* ══ ENTRADA: confirmar tránsito pendiente ══ */}
                          {movTipo === 'entrada' && !selectedTransito && (
                            <div className="space-y-2">
                              {pendingTransitos.length === 0 ? (
                                <div className="text-center py-8 text-zinc-400">
                                  <PackageCheck className="w-10 h-10 mx-auto mb-2" />
                                  <p className="text-sm font-medium">Sin envíos pendientes</p>
                                  <p className="text-xs mt-0.5">No hay tránsitos en camino a este puesto</p>
                                </div>
                              ) : (
                                <>
                                  <p className="text-xs text-zinc-500 font-medium">Selecciona el tránsito a confirmar</p>
                                  {pendingTransitos.map(t => {
                                    const total = Object.values(t.productos).reduce((s, v) => s + Number(v), 0);
                                    return (
                                      <button
                                        key={t.id}
                                        onClick={() => { setSelectedTransito(t); setCantConfirmadas({ ...t.productos }); }}
                                        className="w-full p-3 bg-white border-2 border-zinc-200 rounded-xl text-left hover:border-blue-300 transition-all"
                                      >
                                        <div className="flex items-center justify-between mb-1">
                                          <div className="flex items-center gap-1.5">
                                            <Truck className="w-3.5 h-3.5 text-amber-500" />
                                            <span className="text-sm font-bold text-zinc-900">{t.origen_nombre}</span>
                                          </div>
                                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                            En tránsito
                                          </span>
                                        </div>
                                        <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                                          {Object.entries(t.productos).map(([clave, qty]) => {
                                            const prod = PRODUCTOS_MAP[clave];
                                            return prod ? (
                                              <span key={clave} className="text-xs text-zinc-600">
                                                {prod.nombre}: <strong className="text-zinc-900">{fmtQty(qty)}</strong>
                                              </span>
                                            ) : null;
                                          })}
                                        </div>
                                        <p className="text-xs text-zinc-400 mt-1">
                                          {fmtQty(total)} piezas · Enviado por {t.creado_por_nombre}
                                        </p>
                                      </button>
                                    );
                                  })}
                                </>
                              )}
                            </div>
                          )}

                          {/* ══ CONFIRMAR ENTRADA seleccionada ══ */}
                          {movTipo === 'entrada' && selectedTransito && (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => { setSelectedTransito(null); setCantConfirmadas({}); clearFeedback(); }}
                                  className="text-xs text-blue-600 font-medium hover:underline"
                                >
                                  ← Volver
                                </button>
                                <span className="text-xs text-zinc-500">
                                  Recibiendo de <strong className="text-zinc-800">{selectedTransito.origen_nombre}</strong>
                                </span>
                              </div>

                              <p className="text-xs text-zinc-500">Ajusta las cantidades realmente recibidas.</p>

                              <div className="space-y-0.5">
                                {Object.entries(selectedTransito.productos).map(([clave, enviado]) => {
                                  const prod = PRODUCTOS_MAP[clave];
                                  if (!prod) return null;
                                  const conf = parseFloat(cantConfirmadas[clave] || 0);
                                  const step = prod.allowHalf ? 0.5 : 1;
                                  return (
                                    <div key={clave} className="flex items-center justify-between py-2 border-b border-zinc-100">
                                      <div>
                                        <p className="text-sm font-medium text-zinc-900">{prod.nombre}</p>
                                        <p className="text-xs text-zinc-400">Enviado: {fmtQty(enviado)}</p>
                                      </div>
                                      <div className="flex items-center gap-1.5">
                                        <button
                                          onClick={() => setCantConfirmadas(prev => ({
                                            ...prev, [clave]: Math.max(0, Math.round((conf - step) * 2) / 2)
                                          }))}
                                          disabled={conf === 0}
                                          className="w-9 h-9 flex items-center justify-center border border-zinc-300 rounded-lg disabled:opacity-30 text-zinc-600"
                                        >-</button>
                                        <span className={`w-10 text-center text-xl font-bold ${conf > 0 ? 'text-blue-600' : 'text-zinc-300'}`}>
                                          {fmtQty(conf)}
                                        </span>
                                        <button
                                          onClick={() => setCantConfirmadas(prev => ({
                                            ...prev, [clave]: Math.min(Number(enviado), Math.round((conf + step) * 2) / 2)
                                          }))}
                                          disabled={conf >= Number(enviado)}
                                          className="w-9 h-9 flex items-center justify-center border border-zinc-300 rounded-lg disabled:opacity-30 text-zinc-600"
                                        >+</button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              <button
                                onClick={handleConfirmarEntrada}
                                disabled={entradaSubmitting || Object.values(cantConfirmadas).reduce((s, v) => s + (parseFloat(v) || 0), 0) === 0}
                                className="w-full h-10 bg-blue-600 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
                              >
                                {entradaSubmitting
                                  ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  : <><Check className="w-4 h-4" /> Confirmar recepción</>}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
